import React from "react";
import { render } from "react-dom";
import classNames from "classnames/bind";

import { MobileNav } from "./nav.js";
import { OverallGraphicRep, IndividualGraphicRep } from "./rep_graphics.js";
import { PointerIcon } from "./crayon.js";
import {
  GlyphFilterPerformanceHighest,
  GlyphFilterPerformanceLowest,
  GlyphFilterTrendIncrease,
  GlyphFilterTrendDecrease,
} from "./icons.js";
import { Graph } from "./graph.js";
import {
  RawScore,
  ComboScore,
  ChangeDisplay,
  IntroHeadline,
  IntroParagraph,
  AbstractTrackURL,
} from "./components.js";
import { topSorted, simpleTitleCase } from "./shared.js";
import { PRODUCT_KEYS as S } from "./data_helpers.js";
import data from "./data.js";
import { links } from "./nav.js";
const logo_url = $("header img").attr("src");

// HACK/TODO: these are copy/pasted from report.browserify, find a better home?
const FILTER_HIGHEST = "highest";
const FILTER_LOWEST = "lowest";
const FILTER_INCREASE = "increased_most";
const FILTER_DECREASE = "decreased_most";

let SummaryComboChange = ({ result }) => (
  <span className="summary-combo-change">
    <RawScore result={result} /> <ChangeDisplay result={result} />
  </span>
);

let SummaryScoreDisplay = ({ result: r, showChange: c }) =>
  c ? <SummaryComboChange result={r} /> : <RawScore result={r} />;

let filterInfo = (key, target, measures = null) => {
  let head, icon, rows;
  const iconSize = 30;
  if (key === FILTER_HIGHEST) {
    head = `Where is ${target.name} performing the highest?`;
    icon = (
      <GlyphFilterPerformanceHighest
        square={iconSize}
        backgroundClassName="perf-increase"
      />
    );
    rows = measures && topSorted(measures, "-score", 5);
  } else if (key === FILTER_LOWEST) {
    head = `Where is ${target.name} performing the lowest?`;
    icon = (
      <GlyphFilterPerformanceLowest
        square={iconSize}
        backgroundClassName="perf-decrease"
      />
    );
    rows = measures && topSorted(measures, "+score", 5);
  } else if (key === FILTER_INCREASE) {
    head = `What has improved most for ${target.name}?`;
    icon = (
      <GlyphFilterTrendIncrease
        square={iconSize}
        backgroundClassName="perf-increase"
      />
    );
    rows = measures && topSorted(measures, "-change", 5);
  } else if (key === FILTER_DECREASE) {
    head = `What has decreased most for ${target.name}?`;
    icon = (
      <GlyphFilterTrendDecrease
        square={iconSize}
        backgroundClassName="perf-decrease"
      />
    );
    rows = measures && topSorted(measures, "+change", 5);
  }

  let showChange = key === FILTER_INCREASE || key === FILTER_DECREASE;
  let goodResult = key === FILTER_HIGHEST || key === FILTER_INCREASE;

  return { key, head, icon, rows, showChange, goodResult };
};

let SummaryTable = ({
  filter,
  header = true,
  reportLinks = true,
  selectedSlug,
  setSelectedSlug,
}) => {
  const target = data.target;
  const measures = data.measures.all._children;
  let { head, icon, rows, showChange } = filterInfo(filter, target, measures);
  return (
    <table className={`summary-table filter-${filter}`}>
      {header ? (
        <thead>
          <tr>
            <th>{head}</th>
            <th>
              <div className="summary-icon-bg">{icon}</div>
            </th>
          </tr>
        </thead>
      ) : null}
      <tbody>
        {rows.length ? (
          rows.map((d) => (
            <tr
              key={d.slug}
              className={classNames({
                selected: d.slug === selectedSlug,
              })}
              onClick={
                setSelectedSlug
                  ? (evt) => {
                      setSelectedSlug(d.slug);
                    }
                  : null
              }
            >
              <td>
                <a
                  href={
                    reportLinks
                      ? links.measure_template.replace("_PLACEHOLDER_", d.slug)
                      : null
                  }
                >
                  {d.name}
                </a>
              </td>
              <td>
                <SummaryScoreDisplay
                  showChange={showChange}
                  result={d._results.latest}
                />
              </td>
            </tr>
          ))
        ) : (
          <tr className="empty-list">
            <td colSpan="2">no data available</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

let SummaryGraph = ({
  graphId,
  objects = data.essentials.overall._children,
  activeView = "trends",
  showLegend = true,
  narrowStyle = false,
  selectedObject = null,
  selectObject = () => {},
}) => (
  <Graph
    rankRanges={data.rankRanges}
    survey={data.survey}
    markerStyle={
      narrowStyle
        ? null
        : data.survey.product_key === S.ECE
          ? "selected-primary"
          : "individual-rep"
    }
    allDotsPrimary={data.survey.product_key === S.ECE && !narrowStyle}
    generateLink={(d) =>
      links.essential_template.replace("_PLACEHOLDER_", d.slug)
    }
    config={narrowStyle ? { H: 350, YW: 115, P: 0 } : { H: 300, YW: 115 }}
    {...{
      objects,
      activeView,
      narrowStyle,
      showLegend,
      selectedObject,
      selectObject,
      graphId,
    }}
  />
);

let SummaryList = ({ survey, essentials, selectedSlug, setSelectedSlug }) => {
  // in mobile view, this component replaces the usual graph legend
  const sortedEssentials = topSorted(essentials, "-score", null);
  const showChange = survey._fullYears.length > 1;

  return (
    <ul className="summary-list">
      {sortedEssentials.map((d) => (
        <li
          key={d.slug}
          className={classNames({
            selected: d.slug === selectedSlug,
          })}
          onClick={(evt) => {
            setSelectedSlug(d.slug);
          }}
        >
          <span className="essential">
            <IndividualGraphicRep
              survey={survey}
              item={d}
              result={d._results.latest}
            />
            <span className="name">{d.name}</span>
          </span>
          <SummaryScoreDisplay
            showChange={showChange}
            result={d._results.latest}
          />
        </li>
      ))}
    </ul>
  );
};

class TrackURL extends AbstractTrackURL {
  static basePathname = window.location.pathname;

  updateAppState(urlParts, isInitialize) {
    let { fragParams } = urlParts;
    let { tour } = fragParams;
    if (tour || !isInitialize) {
      if (window.impact_tours) window.impact_tours.setToken(tour || "");
    }
  }

  generateUrlParts() {
    let { toursToken } = this.props;
    let fragParams = {};
    if (toursToken) {
      fragParams["tour"] = toursToken;
    }
    return { fragParams };
  }
}

const DesktopPrintVersion = ({
  survey,
  target,
  essentials,
  overall_organization_statement,
}) => (
  <div className="desktop-print-version">
    <div className="landing-intro">
      <OverallGraphicRep
        size={survey.product_key === S.ECE ? 250 : 175}
        survey={survey}
        essentials={essentials}
        generateLink={(d) =>
          links.essential_template.replace("_PLACEHOLDER_", d.slug)
        }
      />
      <h1>{target.name} Dashboard Summary</h1>
      <IntroHeadline
        leadIn="Overall in"
        WRAPPER="h2"
        {...{ target, survey, overall_organization_statement }}
      />
      <IntroParagraph
        {...{
          target,
          landing_summary: data.landing_summary,
          overall_product: survey.labels.overall_product,
        }}
      />
    </div>
    <div>
      <div className="summary-tables">
        <SummaryTable filter={FILTER_HIGHEST} />
        <SummaryTable filter={FILTER_INCREASE} />
        <SummaryTable filter={FILTER_LOWEST} />
        <SummaryTable filter={FILTER_DECREASE} />
      </div>
      <div className="summary-graph-wrapper">
        <span className="caption">
          <h3>Essential Performance Trends</h3>
        </span>
        <SummaryGraph graphId="desktop" />
      </div>
    </div>
    <div className="second-page">
      <span className="product-name">{essentials.overall.name}</span>
      <img className="logo" src={logo_url} />
      <h2>About the Survey</h2>
      <div className="columns">
        <div dangerouslySetInnerHTML={data.print_content} />
        <div>
          {Object.values(essentials)
            .filter((d) => !d._alias)
            .map((d) => (
              <div key={d.slug}>
                <h3>{d.name}</h3>
                <div dangerouslySetInnerHTML={d.text.overview} />
              </div>
            ))}
        </div>
      </div>
      <div className="footer" dangerouslySetInnerHTML={data.print_footer} />
    </div>
  </div>
);

// c.f. `TableRow` in report.browserify.js
let MobileMeasureTable = ({ measure: d }) => (
  <div className="measure-table">
    <table>
      <tbody>
        <tr>
          <th>Measure</th>
          <td>{d.name}</td>
        </tr>
        <tr>
          <th>Performance</th>
          <td>
            <ComboScore item={d} result={d._results.latest} />
          </td>
        </tr>
        <tr>
          <th>Essential</th>
          <td>
            <IndividualGraphicRep survey={data.survey} item={d._parent} />
            <span className="parent-name">{d._parent.name}</span>
          </td>
        </tr>
        <tr>
          <th>Respondent</th>
          <td>
            <span className="respondent-label">
              {simpleTitleCase(data.subjectLabels[d.subject])}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

let MobileDescriptionPopup = ({ item, onClose }) => (
  <div className="item-description popup">
    <a
      className="close-popup"
      onClick={(evt) => {
        onClose();
      }}
    >
      <span className="glyphicon glyphicon-remove" />
    </a>
    <h1>{item.name}</h1>
    <p className="description" dangerouslySetInnerHTML={item.text.overview} />
    <a
      className="open-main-app"
      target="_blank"
      href={
        item._level === "essentials"
          ? links.essential_template.replace("_PLACEHOLDER_", item.slug)
          : links.measure_template.replace("_PLACEHOLDER_", item.slug)
      }
    >
      View Desktop Version <span className="glyphicon glyphicon-new-window" />
    </a>
  </div>
);

class MobileApp extends React.PureComponent {
  state = {};

  render() {
    let { survey, target, essentials, measures } = this.props;

    let {
      tabKey = "essentials",
      filterKey = FILTER_HIGHEST,
      selectedSlug = null,
      measurePopup = false,
    } = this.state;

    const MAIN_TABS = [
      { key: "essentials", label: "Essentials Overall" },
      { key: "measures", label: "Performance Detail" },
    ];
    const FILTER_OPTS = [
      FILTER_HIGHEST,
      FILTER_INCREASE,
      FILTER_LOWEST,
      FILTER_DECREASE,
    ].map((k) => filterInfo(k, target));
    let selectedFilter = filterInfo(filterKey, target, measures.all._children);
    let selectedMeasure = selectedFilter.rows.find(
      (d) => d.slug === selectedSlug,
    );
    let selectedEssential = essentials[selectedSlug];

    return (
      <div className="mobile-app">
        <MobileNav notesCount={data.notes_count} />
        <ul className="view-tabs">
          {MAIN_TABS.map(({ key, label }) => (
            <li
              key={key}
              className={classNames({
                selected: tabKey === key,
              })}
              onClick={(evt) => {
                this.setState({ tabKey: key });
              }}
            >
              {label}
            </li>
          ))}
        </ul>

        {tabKey === "essentials" ? (
          <div className="main-tab-content essentials-tab">
            <SummaryGraph
              graphId="mobileE"
              showLegend={false}
              selectedObject={selectedEssential}
              selectObject={(lvl, slug) => {
                if (lvl === "essentials") this.setState({ selectedSlug: slug });
                else console.warn("Unexpected click on object", lvl, slug);
              }}
            />
            <SummaryList
              survey={survey}
              essentials={essentials.overall._children}
              selectedSlug={selectedSlug}
              setSelectedSlug={(selectedSlug) => {
                this.setState({ selectedSlug });
              }}
            />
            {selectedEssential ? (
              <MobileDescriptionPopup
                item={selectedEssential}
                onClose={() => {
                  this.setState({ selectedSlug: null });
                }}
              />
            ) : null}
          </div>
        ) : null}
        {tabKey === "measures" ? (
          <div className="main-tab-content measures-tab">
            <ul className="filter-tabs">
              {FILTER_OPTS.map(({ key, icon }) => (
                <li
                  key={key}
                  className={classNames({
                    selected: filterKey === key,
                  })}
                  onClick={(evt) => {
                    this.setState({ filterKey: key });
                  }}
                >
                  {icon}
                </li>
              ))}
            </ul>
            {selectedFilter ? (
              <div className="filter-tab-content">
                <p
                  className={classNames(
                    "filter-question",
                    selectedFilter.goodResult
                      ? "perf-increase"
                      : "perf-decrease",
                  )}
                >
                  {selectedFilter.head}
                </p>
                <div className="result-displays">
                  <div className="scrollable-content">
                    <div className="graph-wrapper">
                      <SummaryGraph
                        graphId="mobileM"
                        showLegend={false}
                        objects={selectedFilter.rows}
                        activeView={
                          selectedFilter.showChange ? "change" : "performance"
                        }
                        narrowStyle={true}
                        selectedObject={selectedMeasure}
                        selectObject={(lvl, slug) => {
                          if (lvl === "measures")
                            this.setState({ selectedSlug: slug });
                          else
                            console.warn(
                              "Unexpected click on object",
                              lvl,
                              slug,
                            );
                        }}
                      />
                    </div>

                    <SummaryTable
                      filter={filterKey}
                      header={false}
                      reportLinks={false}
                      selectedSlug={selectedSlug}
                      setSelectedSlug={(selectedSlug) => {
                        this.setState({ selectedSlug });
                      }}
                    />
                  </div>
                </div>

                {selectedMeasure ? (
                  <div className="measure-details">
                    <MobileMeasureTable measure={selectedMeasure} />
                    <a
                      className="popup-trigger"
                      onClick={(evt) => {
                        evt.preventDefault();
                        this.setState({ measurePopup: true });
                      }}
                    >
                      Details
                    </a>
                  </div>
                ) : (
                  <div className="measure-details none-selected">
                    <div className="style-wrapper">
                      <PointerIcon />
                      <span className="verbal-prompt">
                        Select a measure to learn more.
                      </span>
                    </div>
                  </div>
                )}
                {measurePopup ? (
                  <MobileDescriptionPopup
                    item={selectedMeasure}
                    onClose={() => {
                      this.setState({ measurePopup: false });
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
}

class Summary extends React.PureComponent {
  state = {};

  render() {
    let { data } = this.props;
    let { toursToken } = this.state;

    return (
      <section id="report-body" className="executive-summary">
        <TrackURL {...{ toursToken }} />
        <DesktopPrintVersion {...data} />
        <MobileApp {...data} />
      </section>
    );
  }
}

TrackURL.intializeState(true);
render(
  <Summary
    data={data}
    ref={
      window.impact_tours
        ? (app) => {
            window.impact_tours.watchToken((token) => {
              app.setState({ toursToken: token });
            });
          }
        : null
    }
  />,
  document.querySelector("#react_target"),
);
